import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import Dialog from "../../../library/Dialog";
import { STATUSES, generatePromoCode } from "../partnerShared";
import ActionFormField from "../../../library/ActionFormField";
import useFormFeedback from "../../../library/useFormFeedback";
import { validatePromoCodeForm } from "./formValidation";

const statusLabels = {
  ISSUED: "Available",
  REDEEMED: "Used",
  EXPIRED: "Expired",
};

export default function PromoCodeFormDialog({
  form,
  error,
  campaigns,
  busy,
  onFormChange,
  onClose,
  onReview,
}) {
  const editing = form?.mode === "edit";
  const feedback = useFormFeedback(
    validatePromoCodeForm(form, campaigns),
    form ? `${form.mode}:${form.id || "new"}` : "closed"
  );
  const partner = campaigns.find(
    (item) => item.partner_name === form?.partner_name
  );
  const change = (name, value) =>
    onFormChange((current) => ({ ...current, [name]: value }));

  return (
    <Dialog
      open={Boolean(form)}
      onClose={onClose}
      title={editing ? "Edit promo code" : "Create promo code"}
      subtitle={
        editing
          ? "Update the offer, expiry date, or user linked to this code."
          : "Choose a partner, then use their usual offer or enter a different one."
      }
      icon={<ConfirmationNumberOutlinedIcon />}
      loading={busy}
      confirmText='Review change'
      onConfirm={() => feedback.review(onReview)}
    >
      <Stack spacing={2.5}>
        {error && <Alert severity='error'>{error}</Alert>}
        <ActionFormField
          {...feedback.field("partner_name")}
          select
          label='Partner'
          value={form?.partner_name || ""}
          disabled={busy}
          onChange={(event) => change("partner_name", event.target.value)}
          helperText='Choose the partner this code belongs to.'
        >
          <MenuItem value='' disabled>
            Choose a partner
          </MenuItem>
          {campaigns.map((campaign) => (
            <MenuItem key={campaign.partner_name} value={campaign.partner_name}>
              {campaign.partner_name}
            </MenuItem>
          ))}
        </ActionFormField>
        <ActionFormField
          {...feedback.field("promo_code")}
          label={editing ? "Promo code" : "Promo code (optional)"}
          placeholder='e.g. WELCOME20'
          value={form?.promo_code || ""}
          disabled={busy}
          onChange={(event) =>
            change("promo_code", event.target.value.toUpperCase())
          }
          helperText={
            editing
              ? "Use 4 to 32 letters, numbers or hyphens. Customers enter this code to get the offer."
              : "Leave blank to create a code automatically, or enter 4 to 32 letters, numbers or hyphens."
          }
          action={
            <Button
              variant='outlined'
              disabled={busy}
              onClick={() => change("promo_code", generatePromoCode())}
              sx={{ minHeight: 40, flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Generate code
            </Button>
          }
        />
        <Stack
          component='section'
          aria-label='Discount and validity'
          spacing={2}
          sx={{
            p: 2,
            bgcolor: "action.hover",
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
          }}
        >
          <Box>
            <Typography variant='body2' fontWeight={600}>
              Discount and validity
            </Typography>
            {!editing && partner && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}
              >
                Default offer:{" "}
                <strong>
                  {partner.discount_value}% off · {partner.expiry_days} days
                </strong>{" "}
                (leave blank to apply)
              </Typography>
            )}
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <ActionFormField
              {...feedback.field("discount_value")}
              label={
                editing
                  ? "Discount percentage"
                  : "Discount percentage (optional)"
              }
              placeholder={
                editing
                  ? "e.g. 20"
                  : String(partner?.discount_value ?? "e.g. 20")
              }
              value={form?.discount_value ?? ""}
              disabled={busy}
              inputProps={{ inputMode: "numeric" }}
              onChange={(event) => change("discount_value", event.target.value)}
              helperText={
                editing
                  ? "Enter a whole number from 0 to 100."
                  : partner
                    ? `Leave blank to use ${partner.discount_value}%. Or enter a whole number from 0 to 100.`
                    : "Leave blank to use the partner's discount."
              }
              InputProps={{
                endAdornment: <InputAdornment position='end'>%</InputAdornment>,
              }}
            />
            {!editing && (
              <ActionFormField
                {...feedback.field("expiry_days")}
                label='Valid for (optional)'
                placeholder={String(partner?.expiry_days ?? "e.g. 90")}
                value={form?.expiry_days ?? ""}
                disabled={busy}
                inputProps={{ inputMode: "numeric" }}
                onChange={(event) => change("expiry_days", event.target.value)}
                helperText={
                  partner
                    ? `Leave blank to use ${partner.expiry_days} days. Or enter 1 to 3650 days.`
                    : "Leave blank to use the partner's validity period."
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>days</InputAdornment>
                  ),
                }}
              />
            )}
          </Stack>
          {editing && (
            <ActionFormField
              {...feedback.field("expires_at")}
              label='Expiry date and time'
              type='datetime-local'
              value={form?.expires_at || ""}
              disabled={busy}
              helperText='The code can no longer be used after this date and time.'
              onChange={(event) => change("expires_at", event.target.value)}
            />
          )}
        </Stack>
        <ActionFormField
          {...feedback.field("external_reference_id")}
          label='Partner reference (optional)'
          placeholder='e.g. an order or campaign reference'
          value={form?.external_reference_id || ""}
          disabled={busy}
          onChange={(event) =>
            change("external_reference_id", event.target.value)
          }
          helperText='A reference from the partner to help you find this code later. Up to 128 characters.'
        />
        {editing && (
          <Stack
            spacing={2.5}
            sx={{ pt: 2.5, borderTop: 1, borderColor: "divider" }}
          >
            <ActionFormField
              {...feedback.field("status")}
              select
              label='Code status'
              value={form.status}
              disabled={busy}
              onChange={(event) => change("status", event.target.value)}
              helperText='Available means the code has been issued but has not been used. The expiry date still applies.'
            >
              {STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusLabels[status]}
                </MenuItem>
              ))}
            </ActionFormField>
            <ActionFormField
              label='Linked user ID (optional)'
              value={form.redeemed_by_user_id}
              disabled={busy || form.clearUser}
              onChange={(event) =>
                change("redeemed_by_user_id", event.target.value)
              }
              helperText='Use the account’s user ID, not their email address. Leave this unchanged to keep the current user.'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.clearUser)}
                  disabled={busy || !form.original?.redeemed_by_user_id}
                  onChange={(event) =>
                    change("clearUser", event.target.checked)
                  }
                />
              }
              label={
                <Typography variant='body2'>
                  Remove the current user from this code
                </Typography>
              }
            />
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.applyToSubscription)}
                    disabled={busy}
                    onChange={(event) =>
                      change("applyToSubscription", event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant='body2'>
                    Also update the linked subscription's discount
                  </Typography>
                }
              />
              <Typography variant='caption' color='text.secondary'>
                When on, changing this code's discount also changes the linked
                subscription's discount.
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
